import { useEffect, useState } from "react";
import api, { avatarSrc } from "../lib/api";
import { Link } from "react-router-dom";
import { ARCHETYPES } from "../lib/constants";
import ColorCombo from "../components/ColorCombo";
import { CheckCircle2, XCircle, Inbox, BarChart3, Users, TrendingUp, AlertCircle, Copy, Check, MessageSquareQuote, UserPlus, Pencil, Trash2, X, Save, Loader2, ShieldCheck, Mail, Clock, LayoutGrid, GripVertical, Plus, Eye, EyeOff, Brain, Upload, FileText, ToggleLeft, ToggleRight, Compass } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cjCelebrate } from "../lib/cjCelebrate";

const HUB_ICON_NAMES = ["BookOpen","Library","BarChart3","Heart","DollarSign","Sparkles","Users","FileText","Camera","Shield","Trophy","MessageSquareQuote","Mail","Globe","Star","Calendar","Briefcase","Target","Flag","Award","Lightbulb","Map","Phone","Megaphone","Newspaper","GraduationCap","PieChart","Zap","Wrench","Link","Image","Folder"];
const HUB_COLOR_SWATCHES = ["#f68b1f","#0091be","#7ac143","#ffd400","#083050","#ef4444","#a855f7","#10b981","#f97316","#06b6d4"];
const ROLES = ["super_admin","admin","sales"];

const StatTile = ({ label, value, color, testid, icon: Icon }) => (
  <div className="tc-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="tc-eyebrow text-slate-400">{label}</div>
      {Icon && <Icon size={14} className="text-slate-400" />}
    </div>
    <div className="font-display text-5xl mt-1" style={{ color }}>{value}</div>
  </div>
);

export default function Admin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [notesFor, setNotesFor] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [action, setAction] = useState("approve");
  const [copied, setCopied] = useState(false);
  const [team, setTeam] = useState([]);
  const [editingUser, setEditingUser] = useState(null); // user obj, "new", or null

  const shareUrl = `${window.location.origin}/share`;
  const copyShareLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const loadTeam = () => api.get("/users").then(({ data }) => setTeam(Array.isArray(data) ? data : [])).catch(() => setTeam([]));

  const load = () => api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { load(); loadTeam(); }, []);

  const performAction = async (storyId, mode, notes) => {
    setBusy((b) => ({ ...b, [storyId]: true }));
    try {
      const { data: result } = await api.post(`/stories/${storyId}/${mode}`, { notes: notes || null });
      if (mode === "approve" && result?.celebration) {
        cjCelebrate({
          type: result.celebration.type,
          title: result.celebration.title,
          subtitle: result.celebration.subtitle,
          intensity: result.celebration.intensity || "big",
          icon: "🎉",
        });
      }
      if (mode === "approve" && Array.isArray(result?.new_badges) && result.new_badges.length) {
        // Author earned badges — let admin know in a chill toast
        toast.success(`+${result.new_badges.length} badge${result.new_badges.length > 1 ? "s" : ""} for the author!`);
      }
      await load();
      setNotesFor(null);
      setNotesValue("");
    } finally {
      setBusy((b) => ({ ...b, [storyId]: false }));
    }
  };

  if (!data) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12">
      <div className="tc-eyebrow text-slate-400">Admin · COO</div>
      <h1 className="font-display text-4xl sm:text-5xl text-tc-ink leading-tight">Story command center</h1>
      <p className="text-slate-500 mt-1 text-sm">Approve stories, spot coverage gaps, watch the team rise.</p>

      <div className="tc-card mt-5 p-5 flex items-center gap-4 flex-wrap" data-testid="admin-share-link-card">
        <div className="flex-1 min-w-0">
          <div className="tc-eyebrow text-slate-400">Public client testimonial link</div>
          <div className="font-mono text-xs sm:text-sm text-slate-800 truncate mt-0.5" data-testid="public-share-url">{shareUrl}</div>
          <div className="text-xs text-slate-500 mt-1">Send to clients after a True Colors experience. Submissions land in your queue below.</div>
        </div>
        <button
          onClick={copyShareLink}
          data-testid="copy-share-link"
          className="h-11 px-4 rounded-xl text-white font-bold flex items-center gap-1.5 shadow-sm"
          style={{ background: "#f68b1f" }}
        >
          {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy link</>}
        </button>
        <button
          onClick={async () => {
            if (!window.confirm("Send the weekly digest to the whole team right now?")) return;
            try {
              const { data: r } = await api.post("/admin/digest/send-now");
              toast.success(`Digest sent to ${r.sent} of ${r.total}.`);
            } catch (err) {
              toast.error("Couldn't send digest", { description: err?.response?.data?.detail || err?.message });
            }
          }}
          data-testid="admin-digest-send-now"
          title="The Friday 4 PM ET CJ recap: stories, kudos, points, leaderboard moves, top story of the week. Manual blast — also auto-sends every Friday."
          className="h-11 px-4 rounded-xl border-2 border-slate-200 text-tc-ink font-bold text-sm flex items-center gap-1.5 hover:bg-slate-50"
        >
          <Mail size={14} /> Friday recap email
        </button>
        <button
          onClick={async () => {
            if (!window.confirm("Send the Friday-morning story nudge to the whole team right now?")) return;
            try {
              const { data: r } = await api.post("/admin/digest/nudge/send-now");
              toast.success(`Nudge sent to ${r.sent} of ${r.total}.`);
            } catch (err) {
              toast.error("Couldn't send nudge", { description: err?.response?.data?.detail || err?.message });
            }
          }}
          data-testid="admin-nudge-send-now"
          title="The Friday 10 AM ET nudge: gentle reminder to capture any wins from the week. Manual blast — also auto-sends every Friday."
          className="h-11 px-4 rounded-xl border-2 border-slate-200 text-tc-ink font-bold text-sm flex items-center gap-1.5 hover:bg-slate-50"
        >
          ✨ Friday nudge email
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6">
        <StatTile label="Total stories" value={data.metrics.total_stories} color="#083050" testid="admin-stat-total" icon={BarChart3} />
        <StatTile label="Approved" value={data.metrics.approved_stories} color="#7ac143" testid="admin-stat-approved" icon={CheckCircle2} />
        <StatTile label="Pending" value={data.metrics.pending_approvals} color="#f68b1f" testid="admin-stat-pending" icon={Inbox} />
        <StatTile label="Active reps" value={data.top_contributors.length} color="#0091be" testid="admin-stat-reps" icon={Users} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <section className="tc-card p-6 lg:col-span-2" data-testid="admin-queue">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl flex items-center gap-2"><Inbox size={18} /> Pending queue</h2>
            <span className="tc-pill bg-orange-50 text-orange-700">{data.pending_list.length} waiting</span>
          </div>

          {data.pending_list.length === 0 ? (
            <div className="text-center py-10 text-slate-400">All clear. Inbox zero.</div>
          ) : (
            <div className="mt-4 grid gap-3">
              {data.pending_list.map((s) => (
                <div key={s.id} className="border border-slate-200 rounded-2xl p-4" data-testid={`pending-${s.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="tc-eyebrow text-slate-400">{s.industry || ","} · {s.company_size || ","}</div>
                        {s.source === "client" && (
                          <span className="tc-pill bg-blue-50 text-blue-700"><MessageSquareQuote size={12} /> Client told</span>
                        )}
                      </div>
                      <Link to={`/story/${s.id}`} className="font-display text-lg leading-snug hover:underline line-clamp-2">{s.title || s.challenge_text}</Link>
                      {s.title && s.challenge_text && (
                        <div className="text-xs text-slate-500 line-clamp-2 mt-1"><span className="font-bold uppercase tracking-widest mr-1.5">Challenge</span>{s.challenge_text}</div>
                      )}
                      <div className="text-sm text-slate-600 line-clamp-2 mt-1">{s.after_text}</div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 flex-wrap">
                        <span>{s.source === "client" ? "From" : "By"} <strong>{s.client_submitter_name || s.author_name || ","}</strong>{s.client_submitter_org ? <> at <strong>{s.client_submitter_org}</strong></> : null}</span>
                        {s.other_rep_name && <span className="tc-pill bg-orange-50 text-orange-700">Rep: {s.other_rep_name} (off-platform)</span>}
                        {s.client_quote && <span className="tc-pill bg-yellow-50 text-yellow-800">Quote</span>}
                        {s.media?.length > 0 && <span className="tc-pill bg-blue-50 text-blue-700">{s.media.length} media</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => performAction(s.id, "approve", null)}
                        disabled={busy[s.id]}
                        data-testid={`approve-${s.id}`}
                        className="h-10 px-4 rounded-xl text-white font-bold flex items-center gap-1.5"
                        style={{ background: "#7ac143" }}
                      >
                        <CheckCircle2 size={16} /> Approve
                      </button>
                      {s.source !== "client" && (
                        <button
                          onClick={() => { setNotesFor(s.id); setAction("reject"); setNotesValue(""); }}
                          data-testid={`reject-${s.id}`}
                          className="h-10 px-4 rounded-xl border border-slate-200 font-bold flex items-center gap-1.5 hover:bg-slate-50"
                        >
                          <XCircle size={16} /> Send back
                        </button>
                      )}
                    </div>
                  </div>

                  {notesFor === s.id && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="What needs to change before this can go live?"
                        data-testid={`reject-notes-${s.id}`}
                        className="w-full min-h-[80px] rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none p-3 text-sm"
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => performAction(s.id, action, notesValue)}
                          data-testid={`confirm-reject-${s.id}`}
                          className="h-10 px-4 rounded-xl text-white font-bold"
                          style={{ background: "#f68b1f" }}
                        >Send back with notes</button>
                        <button onClick={() => setNotesFor(null)} className="h-10 px-4 rounded-xl border border-slate-200 font-bold">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="grid gap-4 content-start">
          {/* Sent-back queue, stories Theresa rejected and is waiting on the rep to fix */}
          {(data.awaiting_fixes_list || []).length > 0 && (
            <div className="tc-card p-6 border-2 border-amber-200 bg-amber-50/40" data-testid="admin-awaiting-fixes">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-display text-xl flex items-center gap-2 text-amber-900">
                  <Clock size={18} /> Waiting on edits
                </h3>
                <span className="tc-pill bg-amber-100 text-amber-800">{data.awaiting_fixes_list.length} out</span>
              </div>
              <p className="text-xs text-amber-800/80 mt-1">You sent these back. The rep needs to update and resubmit.</p>
              <div className="mt-3 grid gap-2">
                {data.awaiting_fixes_list.map((s) => (
                  <Link
                    to={`/story/${s.id}`}
                    key={s.id}
                    data-testid={`admin-awaiting-row-${s.id}`}
                    className="block rounded-xl p-3 bg-white border border-amber-200 hover:border-amber-400 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm text-tc-ink leading-tight line-clamp-2">{s.title || s.challenge_text || "Untitled story"}</div>
                        <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span>{s.author_name || "—"}</span>
                          <span>·</span>
                          <span>{s.industry || "—"}</span>
                          {s.updated_at && (<><span>·</span><span>Sent back {s.updated_at.slice(0, 10)}</span></>)}
                        </div>
                        {s.admin_notes && (
                          <div className="text-[11px] text-amber-900 bg-amber-100/60 rounded-md px-2 py-1 mt-1.5 line-clamp-2">
                            <span className="font-bold mr-1">Your note:</span>{s.admin_notes}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const ok = window.confirm(`Approve "${(s.title || s.challenge_text || "this story").slice(0, 60)}" now?`);
                            if (!ok) return;
                            try {
                              await performAction(s.id, "approve", null);
                              toast.success("Approved.");
                            } catch (err) {
                              toast.error("Couldn't approve", { description: err?.response?.data?.detail || err?.message });
                            }
                          }}
                          data-testid={`admin-awaiting-approve-${s.id}`}
                          title="Approve this story (skip the rep)"
                          className="h-7 px-2 rounded-lg bg-green-600 text-white font-bold text-[11px] hover:bg-green-700 inline-flex items-center gap-1"
                        >
                          <CheckCircle2 size={11} /> Approve
                        </button>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const ok = window.confirm(`Nudge ${(s.author_name || "the rep").split(" ")[0]} again via email?`);
                            if (!ok) return;
                            try {
                              await api.post(`/admin/stories/${s.id}/nudge`);
                              toast.success("Nudge sent.");
                            } catch (err) {
                              toast.error("Couldn't send nudge", { description: err?.response?.data?.detail || err?.message });
                            }
                          }}
                          data-testid={`admin-awaiting-nudge-${s.id}`}
                          title="Send a reminder email to the rep"
                          className="h-7 px-2 rounded-lg border border-amber-300 text-amber-800 font-bold text-[11px] hover:bg-amber-100"
                        >
                          Nudge
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="tc-card p-6" data-testid="admin-top-contributors">
            <h3 className="font-display text-xl flex items-center gap-2"><TrendingUp size={18} /> Top contributors</h3>
            <div className="mt-3 grid gap-2">
              {data.top_contributors.slice(0, 5).map((u, i) => {
                const a = ARCHETYPES[u.archetype] || ARCHETYPES.blue;
                return (
                  <div key={u.id} className="flex items-center gap-3 border border-slate-100 rounded-xl p-2">
                    <div className="w-6 text-center font-display text-lg text-slate-400">{i + 1}</div>
                    <div className="w-8 h-8 rounded-full bg-cover bg-center border-2" style={{ backgroundImage: `url(${avatarSrc(u)})`, borderColor: a.color }} />
                    <div className="text-sm font-bold flex-1 min-w-0">
                      <div className="truncate">{u.name}</div>
                      <ColorCombo user={u} variant="compact" className="mt-0.5" />
                    </div>
                    <div className="font-display text-lg" style={{ color: a.color }}>{u.points || 0}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="tc-card p-6" data-testid="admin-coverage">
            <h3 className="font-display text-xl flex items-center gap-2"><AlertCircle size={18} /> Coverage gaps</h3>
            <div className="mt-3">
              <div className="tc-eyebrow text-slate-500 mb-1">By industry</div>
              <div className="flex flex-wrap gap-1.5">
                {data.by_industry.length === 0 && <div className="text-sm text-slate-400">No approved stories yet</div>}
                {data.by_industry.map((r) => (
                  <span key={r.industry} className="tc-pill bg-slate-100 text-slate-700">{r.industry} <strong className="ml-1 text-tc-ink">{r.count}</strong></span>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="tc-eyebrow text-slate-500 mb-1">By challenge</div>
              <div className="flex flex-wrap gap-1.5">
                {data.by_challenge.length === 0 && <div className="text-sm text-slate-400">No data yet</div>}
                {data.by_challenge.map((r) => (
                  <span key={r.challenge} className="tc-pill bg-slate-100 text-slate-700">{r.challenge} <strong className="ml-1 text-tc-ink">{r.count}</strong></span>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Hub tiles management, super admin only */}
      <HubTilesEditor />

      {/* CJ Knowledge docs — super admin uploads */}
      <CJKnowledgeEditor />

      {/* Tavily lead-search usage, super admin only */}
      <LeadSearchUsageCard />

      {/* Team management, super admin only */}
      <section className="tc-card mt-8 p-6" data-testid="admin-team-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="tc-eyebrow text-slate-400 flex items-center gap-1.5"><ShieldCheck size={12} /> Team & roles</div>
            <h2 className="font-display text-2xl text-tc-ink mt-1">Who's on CJ</h2>
            <p className="text-xs text-slate-500 mt-1">Add a teammate, change a role, or remove access. Only super-admins see this section.</p>
          </div>
          <button
            onClick={() => setEditingUser("new")}
            data-testid="admin-add-user"
            className="h-11 px-4 rounded-2xl bg-tc-ink text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
          >
            <UserPlus size={16} /> Add teammate
          </button>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-400 font-bold border-b border-slate-200">
                <th className="text-left py-2.5 font-bold">Name</th>
                <th className="text-left py-2.5 font-bold">Email</th>
                <th className="text-left py-2.5 font-bold">Role</th>
                <th className="text-left py-2.5 font-bold">Points</th>
                <th className="py-2.5"></th>
              </tr>
            </thead>
            <tbody data-testid="admin-team-table">
              {team.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 group" data-testid={`admin-team-row-${u.id}`}>
                  <td className="py-3 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-cover bg-center bg-slate-200" style={{ backgroundImage: `url(${avatarSrc(u)})` }} />
                    <span className="font-bold text-tc-ink">{u.name}</span>
                  </td>
                  <td className="py-3 text-slate-500 text-xs sm:text-sm">{u.email || ","}</td>
                  <td className="py-3"><RolePill role={u.role} /></td>
                  <td className="py-3 font-bold text-tc-ink">{u.points || 0}</td>
                  <td className="py-3 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={async () => {
                        const ok = window.confirm(`Send a CJ welcome email + sign-in link to ${u.name}?`);
                        if (!ok) return;
                        setBusy((b) => ({ ...b, [`welcome-${u.id}`]: true }));
                        try {
                          await api.post(`/admin/users/${u.id}/welcome`);
                          toast.success(`Welcome email sent to ${u.name.split(" ")[0]}.`);
                        } catch (err) {
                          toast.error("Couldn't send welcome", { description: err?.response?.data?.detail || err?.message });
                        } finally {
                          setBusy((b) => ({ ...b, [`welcome-${u.id}`]: false }));
                        }
                      }}
                      data-testid={`admin-welcome-user-${u.id}`}
                      title="Send welcome email + sign-in link"
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40"
                      disabled={!!busy[`welcome-${u.id}`]}
                    >
                      {busy[`welcome-${u.id}`] ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    </button>
                    <button onClick={() => setEditingUser(u)} data-testid={`admin-edit-user-${u.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Edit">
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {team.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400">No teammates yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <AnimatePresence>
        {editingUser && (
          <UserModal
            user={editingUser === "new" ? null : editingUser}
            onClose={() => setEditingUser(null)}
            onSaved={() => { setEditingUser(null); loadTeam(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function HubTilesEditor() {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // tile object or "new"

  const load = () =>
    api.get("/admin/hub/tiles")
      .then(({ data }) => setTiles(Array.isArray(data) ? data : []))
      .catch(() => setTiles([]))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    const next = [...tiles];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setTiles(next);
    try {
      await api.post("/admin/hub/tiles/reorder", { ids: next.map((t) => t.id) });
    } catch {
      toast.error("Couldn't reorder. Reloading.");
      load();
    }
  };

  const removeTile = async (t) => {
    if (!window.confirm(`Delete the "${t.title}" tile from the Hub?`)) return;
    try {
      await api.delete(`/admin/hub/tiles/${t.id}`);
      toast.success("Tile removed.");
      load();
    } catch (err) {
      toast.error("Couldn't delete", { description: err?.response?.data?.detail || err?.message });
    }
  };

  return (
    <section className="tc-card mt-8 p-6" data-testid="admin-hub-tiles">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tc-eyebrow text-slate-400 flex items-center gap-1.5"><LayoutGrid size={12} /> Hub layout</div>
          <h2 className="font-display text-2xl text-tc-ink mt-1">Hub tiles</h2>
          <p className="text-xs text-slate-500 mt-1">Add, edit, reorder or hide tiles on the main Hub. Choose who sees each one.</p>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="admin-hub-add-tile"
          className="h-11 px-4 rounded-2xl bg-tc-ink text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
        >
          <Plus size={16} /> Add tile
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm mt-6">Loading tiles…</div>
      ) : (
        <div className="mt-5 grid gap-2">
          {tiles.map((t, idx) => {
            const Icon = LucideIcons[t.icon] || LucideIcons.Sparkles;
            const isExternal = /^https?:\/\//i.test(t.url || "");
            return (
              <div
                key={t.id}
                data-testid={`admin-hub-tile-row-${t.id}`}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors"
              >
                <div className="flex flex-col gap-0.5 text-slate-400">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up" className="hover:text-tc-ink disabled:opacity-30 leading-none">▲</button>
                  <button onClick={() => move(idx, 1)} disabled={idx === tiles.length - 1} title="Move down" className="hover:text-tc-ink disabled:opacity-30 leading-none">▼</button>
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.color, color: "#fff" }}>
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-tc-ink text-sm">{t.title}</span>
                    {!t.available && <span className="tc-pill bg-slate-100 text-slate-600 text-[10px]">Hidden</span>}
                    {isExternal && <span className="tc-pill bg-blue-50 text-blue-700 text-[10px]">External</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">{t.url} · seen by {t.visible_to?.join(", ") || "everyone"}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setEditing(t)}
                    data-testid={`admin-hub-tile-edit-${t.id}`}
                    className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-100"
                    title="Edit tile"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => removeTile(t)}
                    data-testid={`admin-hub-tile-delete-${t.id}`}
                    className="h-8 w-8 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50"
                    title="Delete tile"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
          {tiles.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">No tiles yet — add one above.</div>}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <HubTileModal
            tile={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function HubTileModal({ tile, onClose, onSaved }) {
  const isNew = !tile;
  const [draft, setDraft] = useState(tile || {
    title: "", blurb: "", icon: "Sparkles", color: "#f68b1f",
    url: "/", visible_to: [...ROLES], available: true,
  });
  const [saving, setSaving] = useState(false);

  const toggleRole = (r) => {
    setDraft((d) => {
      const cur = d.visible_to || [];
      const has = cur.includes(r);
      return { ...d, visible_to: has ? cur.filter((x) => x !== r) : [...cur, r] };
    });
  };

  const save = async () => {
    if (!draft.title?.trim()) return toast.error("Title is required.");
    if (!draft.url?.trim()) return toast.error("URL is required.");
    if (!draft.visible_to?.length) return toast.error("Pick at least one role that can see it.");
    setSaving(true);
    const body = {
      title: draft.title.trim(),
      blurb: (draft.blurb || "").trim(),
      icon: draft.icon || "Sparkles",
      color: draft.color || "#f68b1f",
      url: draft.url.trim(),
      visible_to: draft.visible_to,
      available: draft.available,
    };
    try {
      if (isNew) {
        await api.post("/admin/hub/tiles", body);
        toast.success("Tile added.");
      } else {
        await api.put(`/admin/hub/tiles/${tile.id}`, body);
        toast.success("Tile saved.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      setSaving(false);
    }
  };

  const PreviewIcon = LucideIcons[draft.icon] || LucideIcons.Sparkles;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="hub-tile-modal"
    >
      <motion.div
        initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 10 }}
        className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl text-tc-ink">{isNew ? "Add tile" : "Edit tile"}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
        </div>

        {/* Live preview */}
        <div className="mt-4 rounded-2xl border-2 p-3 flex items-center gap-3 min-h-[72px]" style={{ borderColor: draft.color }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: draft.color }}>
            <PreviewIcon size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-base text-tc-ink line-clamp-1">{draft.title || "Tile title"}</div>
            <div className="text-[11px] text-slate-500 line-clamp-1">{draft.blurb || "Short description shows on hover"}</div>
          </div>
        </div>

        <div className="grid gap-4 mt-5">
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Title</span>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={60} data-testid="hub-tile-title" className="w-full h-11 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm font-bold" />
          </label>
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Short description</span>
            <input value={draft.blurb || ""} onChange={(e) => setDraft({ ...draft, blurb: e.target.value })} maxLength={140} placeholder="Shows on hover" data-testid="hub-tile-blurb" className="w-full h-11 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm" />
          </label>
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">URL</span>
            <input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="/library  or  https://docs.google.com/…" data-testid="hub-tile-url" className="w-full h-11 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm font-mono" />
            <span className="text-[11px] text-slate-400 mt-1 block">Internal routes start with <code>/</code>. External links start with <code>https://</code> and open in a new tab.</span>
          </label>

          <div>
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Color</span>
            <div className="flex flex-wrap gap-2">
              {HUB_COLOR_SWATCHES.map((c) => (
                <button
                  key={c}
                  onClick={() => setDraft({ ...draft, color: c })}
                  type="button"
                  data-testid={`hub-tile-color-${c.replace("#","")}`}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${draft.color === c ? "scale-110 ring-2 ring-offset-2 ring-tc-ink" : "border-white hover:scale-105"}`}
                  style={{ background: c, borderColor: draft.color === c ? "#fff" : c }}
                  title={c}
                />
              ))}
              <input
                type="color"
                value={draft.color}
                onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                className="w-8 h-8 rounded-lg cursor-pointer"
                title="Custom color"
              />
            </div>
          </div>

          <div>
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Icon</span>
            <div className="grid grid-cols-8 gap-1.5 max-h-32 overflow-y-auto p-2 rounded-xl border border-slate-200">
              {HUB_ICON_NAMES.map((name) => {
                const I = LucideIcons[name];
                if (!I) return null;
                const picked = draft.icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setDraft({ ...draft, icon: name })}
                    data-testid={`hub-tile-icon-${name}`}
                    title={name}
                    className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${picked ? "bg-tc-ink text-white" : "text-slate-500 hover:bg-slate-100"}`}
                  >
                    <I size={15} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Visible to</span>
            <div className="flex gap-2 flex-wrap">
              {ROLES.map((r) => {
                const picked = (draft.visible_to || []).includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRole(r)}
                    data-testid={`hub-tile-role-${r}`}
                    className={`tc-pill border ${picked ? "bg-tc-ink text-white border-tc-ink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  >
                    {r.replace("_", " ")}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.available !== false}
              onChange={(e) => setDraft({ ...draft, available: e.target.checked })}
              data-testid="hub-tile-available"
              className="w-4 h-4 accent-tc-ink"
            />
            <span className="text-sm">
              {draft.available !== false
                ? <span className="text-tc-ink font-bold inline-flex items-center gap-1"><Eye size={13} /> Visible on Hub</span>
                : <span className="text-slate-400 inline-flex items-center gap-1"><EyeOff size={13} /> Hidden (still saved, just dim/locked)</span>}
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="h-11 px-4 rounded-2xl border border-slate-200 font-bold text-sm hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="hub-tile-save" className="h-11 px-5 rounded-2xl bg-tc-ink text-white font-bold flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? "Add tile" : "Save"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function RolePill({ role }) {
  const map = {
    super_admin: { label: "Super Admin", color: "#7c2d12", bg: "#FED7AA" },
    admin: { label: "Admin", color: "#5b21b6", bg: "#EDE9FE" },
    sales: { label: "Sales", color: "#1d4ed8", bg: "#DBEAFE" },
  };
  const r = map[role] || map.sales;
  return (
    <span className="inline-flex items-center text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full" style={{ color: r.color, background: r.bg }}>
      {r.label}
    </span>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const isNew = !user;
  const [draft, setDraft] = useState(user || {
    email: "",
    name: "",
    role: "sales",
    archetype: "blue",
    archetype_secondary: "",
    spectrum: [],
    show_on_public_form: true,
    receive_team_emails: true,
  });
  const [saving, setSaving] = useState(false);

  // Initialise the spectrum from legacy archetype/secondary if it's empty so
  // editing an old account doesn't wipe their color combo.
  const initialSpectrum = Array.isArray(draft.spectrum) && draft.spectrum.length
    ? draft.spectrum
    : [draft.archetype, draft.archetype_secondary].filter(Boolean);
  const [spectrum, setSpectrum] = useState(initialSpectrum);

  const toggleColor = (k) => {
    setSpectrum((cur) => {
      if (cur.includes(k)) return cur.filter((x) => x !== k);
      if (cur.length >= 4) return cur;
      return [...cur, k];
    });
  };

  const save = async () => {
    if (!draft.name?.trim()) return toast.error("Name is required.");
    if (isNew && !draft.email?.trim()) return toast.error("Email is required.");
    if (spectrum.length === 0) return toast.error("Pick at least one color for the spectrum.");
    setSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        role: draft.role,
        archetype: spectrum[0],
        archetype_secondary: spectrum[1] || null,
        spectrum,
        show_on_public_form: draft.show_on_public_form !== false,
        receive_team_emails: draft.receive_team_emails !== false,
      };
      if (isNew) {
        body.email = draft.email.trim().toLowerCase();
        // Ask first whether to expose them on the public testimonial form.
        if (!window.confirm(
          `Add ${body.name} to CJ.\n\nShould clients be able to pick ${body.name.split(" ")[0]} on the public testimonial form? Click OK for yes, Cancel to hide them (e.g. for execs who have CJ access but aren't client-facing).`
        )) {
          body.show_on_public_form = false;
        } else {
          body.show_on_public_form = true;
        }
        await api.post("/admin/users", body);
        toast.success(`${body.name} added${body.show_on_public_form ? "" : " (hidden from public form)"}.`);
      } else {
        await api.put(`/admin/users/${user.id}`, body);
        toast.success("Saved.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Remove ${user.name}? Their stories will be re-attributed to you.`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("Removed.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-user-modal">
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={16} /></button>
        <h2 className="font-display text-2xl text-tc-ink">{isNew ? "Add teammate" : "Edit teammate"}</h2>

        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Full name *</span>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={ADMIN_INPUT} data-testid="admin-user-name" />
          </label>
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Email *</span>
            <input type="email" disabled={!isNew} value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={`${ADMIN_INPUT} ${!isNew ? "bg-slate-50 text-slate-500" : ""}`} data-testid="admin-user-email" />
            {!isNew && <span className="text-[10px] text-slate-400 mt-1 block">Email can't be changed after creation.</span>}
          </label>
          <label className="block">
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">Role</span>
            <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className={ADMIN_INPUT} data-testid="admin-user-role">
              <option value="sales">Sales</option>
              <option value="admin">Admin (facilitator edits)</option>
              <option value="super_admin">Super Admin (full access)</option>
            </select>
          </label>
          <div>
            <span className="tc-eyebrow text-slate-500 mb-1.5 block">True Colors spectrum</span>
            <p className="text-[11px] text-slate-500 mb-2">Tap colors in order, dominant first. Tap again to remove. You can pick 1–4.</p>

            {/* Current ordered spectrum preview */}
            <div className="flex items-center gap-1.5 flex-wrap mb-2 min-h-[28px]" data-testid="admin-user-spectrum-preview">
              {spectrum.length === 0 && (
                <span className="text-xs text-slate-400 italic">No colors picked yet</span>
              )}
              {spectrum.map((k, i) => {
                const a = ARCHETYPES[k];
                return (
                  <span
                    key={`${k}-${i}`}
                    className="inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-bold text-white"
                    style={{ background: a.color }}
                  >
                    <span className="opacity-80">{i + 1}.</span> {a.title.replace("The ", "")}
                  </span>
                );
              })}
              {spectrum.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSpectrum([])}
                  data-testid="admin-user-spectrum-clear"
                  className="text-[11px] font-bold text-slate-500 hover:text-tc-ink underline"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Pickable color buttons */}
            <div className="grid grid-cols-4 gap-2">
              {Object.keys(ARCHETYPES).map((k) => {
                const a = ARCHETYPES[k];
                const idx = spectrum.indexOf(k);
                const picked = idx !== -1;
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => toggleColor(k)}
                    data-testid={`admin-user-color-${k}`}
                    className="relative h-14 rounded-xl border-2 flex flex-col items-center justify-center text-[10px] font-extrabold uppercase tracking-widest transition-all"
                    style={{
                      background: picked ? a.color : "#fff",
                      color: picked ? "#fff" : a.text,
                      borderColor: a.color,
                      boxShadow: picked ? "0 4px 10px rgba(8,48,80,0.15)" : "none",
                    }}
                  >
                    {picked && (
                      <span className="absolute top-1 left-1.5 w-5 h-5 rounded-full bg-white/90 text-[11px] flex items-center justify-center" style={{ color: a.color }}>
                        {idx + 1}
                      </span>
                    )}
                    {a.title.replace("The ", "")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {!isNew && (
          <label className="flex items-start gap-2 mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50/60 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.show_on_public_form !== false}
              onChange={(e) => setDraft({ ...draft, show_on_public_form: e.target.checked })}
              data-testid="admin-user-public-form-toggle"
              className="mt-0.5 w-4 h-4 accent-tc-ink"
            />
            <span className="text-sm text-slate-700">
              <span className="font-bold text-tc-ink">Visible on the public testimonial form</span>
              <span className="block text-[11px] text-slate-500 mt-0.5">When OFF, clients won't be able to pick this person from the rep dropdown. Use for execs/leaders who have CJ access but aren't client-facing.</span>
            </span>
          </label>
        )}

        {!isNew && (
          <label className="flex items-start gap-2 mt-3 p-3 rounded-xl border border-slate-200 bg-slate-50/60 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.receive_team_emails !== false}
              onChange={(e) => setDraft({ ...draft, receive_team_emails: e.target.checked })}
              data-testid="admin-user-team-emails-toggle"
              className="mt-0.5 w-4 h-4 accent-tc-ink"
            />
            <span className="text-sm text-slate-700">
              <span className="font-bold text-tc-ink">Receives team-wide emails</span>
              <span className="block text-[11px] text-slate-500 mt-0.5">When ON, this user gets the weekly digest and Friday nudge. Turn OFF for leaders (like Robert) who only want story-approval notifications, not team stats.</span>
            </span>
          </label>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
          {!isNew ? (
            <button onClick={remove} data-testid="admin-user-delete" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl text-red-600 hover:bg-red-50 font-bold text-sm">
              <Trash2 size={14} /> Remove
            </button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 h-10 rounded-xl font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="admin-user-save" className="px-4 h-10 rounded-xl bg-tc-ink text-white font-bold flex items-center gap-2 disabled:opacity-60">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving</> : <><Save size={14} /> {isNew ? "Add" : "Save"}</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const ADMIN_INPUT = "w-full h-11 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm";

/* ---------------------------- CJ Knowledge ------------------------------ */

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1000) return `${n} chars`;
  return `${(n / 1000).toFixed(1)}k chars`;
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function CJKnowledgeEditor() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    api.get("/admin/cj-knowledge")
      .then(({ data }) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset the input so same file can be re-selected
    if (!file) return;
    const okExt = /\.(docx|pdf|txt|md)$/i.test(file.name);
    if (!okExt) {
      toast.error("Use a .docx, .pdf, .txt or .md file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("That file is over 10 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/cj-knowledge/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Added to CJ's knowledge.");
      load();
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setUploading(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.patch(`/admin/cj-knowledge/${d.id}`, { is_active: !d.is_active });
      setDocs((prev) => prev.map((x) => x.id === d.id ? { ...x, is_active: !x.is_active } : x));
    } catch (err) {
      toast.error("Couldn't update", { description: err?.response?.data?.detail || err?.message });
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}" from CJ's knowledge? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/cj-knowledge/${d.id}`);
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error("Couldn't delete", { description: err?.response?.data?.detail || err?.message });
    }
  };

  const activeCount = docs.filter((d) => d.is_active).length;
  const totalChars = docs.filter((d) => d.is_active).reduce((a, d) => a + (d.chars || 0), 0);

  return (
    <section className="tc-card mt-8 p-6" data-testid="admin-cj-knowledge">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tc-eyebrow text-slate-400 flex items-center gap-1.5"><Brain size={12} /> CJ's brain</div>
          <h2 className="font-display text-2xl text-tc-ink mt-1">Knowledge documents</h2>
          <p className="text-xs text-slate-500 mt-1">
            Upload policies, FAQs, agreements, playbooks — CJ reads active docs before drafting every email.{" "}
            <strong>{activeCount}</strong> active · {fmtBytes(totalChars)} total.
          </p>
        </div>
        <label
          className={`h-11 px-4 rounded-xl text-white font-bold text-sm flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 cursor-wait" : "hover:brightness-110"}`}
          style={{ background: "#f68b1f" }}
          data-testid="cj-knowledge-upload-trigger"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload document"}
          <input
            type="file"
            accept=".docx,.pdf,.txt,.md"
            onChange={onFileChange}
            disabled={uploading}
            className="hidden"
            data-testid="cj-knowledge-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 mt-6">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="mt-6 p-6 rounded-xl border-2 border-dashed border-slate-200 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No documents yet. Upload a policy, FAQ, or playbook to shape every email CJ writes.</div>
        </div>
      ) : (
        <ul className="mt-5 divide-y divide-slate-100" data-testid="cj-knowledge-list">
          {docs.map((d) => (
            <li key={d.id} className="py-3 flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-tc-ink truncate flex items-center gap-2">
                  {d.title}
                  {!d.is_active && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {d.filename} · {fmtBytes(d.chars)} · {d.uploaded_by_name || "—"} · {fmtDate(d.uploaded_at)}
                </div>
              </div>
              <button
                onClick={() => toggle(d)}
                data-testid={`cj-knowledge-toggle-${d.id}`}
                title={d.is_active ? "CJ reads this. Click to pause it." : "CJ is ignoring this. Click to activate."}
                className="shrink-0 p-1 text-slate-500 hover:text-tc-orange transition-colors"
              >
                {d.is_active
                  ? <ToggleRight className="w-7 h-7" style={{ color: "#7ac143" }} />
                  : <ToggleLeft className="w-7 h-7 text-slate-300" />}
              </button>
              <button
                onClick={() => remove(d)}
                data-testid={`cj-knowledge-delete-${d.id}`}
                className="shrink-0 p-2 text-slate-400 hover:text-red-500 transition-colors"
                title="Delete this document"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}




/* -------------------------- Lead-search usage -------------------------- */

function LeadSearchUsageCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/leads/admin/usage")
      .then(({ data }) => setUsage(data))
      .catch(() => setUsage(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!usage) return null;

  const barColor =
    usage.level === "exceeded" ? "#ef4444" :
    usage.level === "alert"    ? "#f59e0b" :
    usage.level === "warn"     ? "#f68b1f" : "#7ac143";
  const pct = Math.min(100, usage.percent || 0);

  return (
    <section className="tc-card mt-8 p-6" data-testid="admin-lead-usage">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="tc-eyebrow text-slate-400 flex items-center gap-1.5"><Compass size={12} /> Find Leads</div>
          <h2 className="font-display text-2xl text-tc-ink mt-1">Monthly Tavily usage</h2>
          <p className="text-xs text-slate-500 mt-1">
            Tracks the team's lead-search burn against the 1,500 free credits per month. Resets {usage.resets_on}.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black" style={{ color: barColor }}>{Math.round(pct)}%</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">of free tier</div>
        </div>
      </div>
      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <div className="mt-3 text-sm">
        <strong className="text-tc-ink">{usage.used.toLocaleString()}</strong>
        <span className="text-slate-500"> / {usage.limit.toLocaleString()} searches used
          &middot; <strong className="text-tc-ink">{usage.remaining.toLocaleString()}</strong> left</span>
      </div>
    </section>
  );
}
